/**
 * PCNGuard FleetMode fixed-plan pricing.
 *
 * Paid plans include unlimited PCNs commercially. Usage and AI records remain
 * internal telemetry for cost controls and support follow-up, not customer
 * metered billing.
 */

export type PlanId = 'starter' | 'growth' | 'pro_fleet' | 'concierge';
export type LegacyPlanId = 'pro' | 'managed';
export type AnyPlanId = PlanId | LegacyPlanId | 'trial';
export type BillingCycle = 'monthly' | 'yearly';

export const BETA_ANNUAL_DISCOUNT_PERCENT = 30;

const betaAnnualFee = (monthlyFee: number): number =>
  Math.round(monthlyFee * 12 * (1 - BETA_ANNUAL_DISCOUNT_PERCENT / 100));

export interface PlanConfig {
  id: PlanId;
  name: string;
  shortName: string;
  description: string;
  monthlyFee: number | null;
  yearlyFee: number | null;
  seats: string;
  recommendedFor: string;
  monthlySoftLimit: number | null;
  dailySoftLimit: number | null;
  features: {
    aiExtraction: boolean;
    bulkUpload: boolean;
    riskScoring: boolean;
    dvlaLookup: boolean;
    manualLiabilityTransfer: boolean;
    basicDashboard: boolean;
    advancedReports: boolean;
    aiAppeals: boolean;
    oneClickTransfer: boolean;
    prioritySupport: boolean;
    concierge: boolean;
    multipleTeamMembers: boolean;
    agreementAudit: boolean;
  };
}

export const PRICING = {
  PLANS: {
    STARTER: {
      id: 'starter',
      name: 'FleetMode Starter',
      shortName: 'Starter',
      description: 'For a small PCO hire team proving a controlled PCN workflow.',
      monthlyFee: 79,
      yearlyFee: betaAnnualFee(79),
      seats: '1 user',
      recommendedFor: 'Small operators and owner-led teams',
      monthlySoftLimit: 100,
      dailySoftLimit: 20,
      features: {
        aiExtraction: true,
        bulkUpload: true,
        riskScoring: true,
        dvlaLookup: false,
        manualLiabilityTransfer: true,
        basicDashboard: true,
        advancedReports: false,
        aiAppeals: false,
        oneClickTransfer: false,
        prioritySupport: false,
        concierge: false,
        multipleTeamMembers: false,
        agreementAudit: false,
      },
    },
    GROWTH: {
      id: 'growth',
      name: 'FleetMode Growth',
      shortName: 'Growth',
      description: 'For teams handling PCNs every week and needing shared workflow.',
      monthlyFee: 179,
      yearlyFee: betaAnnualFee(179),
      seats: '3 team seats',
      recommendedFor: 'Growing PCO hire companies',
      monthlySoftLimit: 300,
      dailySoftLimit: 50,
      features: {
        aiExtraction: true,
        bulkUpload: true,
        riskScoring: true,
        dvlaLookup: true,
        manualLiabilityTransfer: true,
        basicDashboard: true,
        advancedReports: true,
        aiAppeals: true,
        oneClickTransfer: true,
        prioritySupport: true,
        concierge: false,
        multipleTeamMembers: true,
        agreementAudit: true,
      },
    },
    PRO_FLEET: {
      id: 'pro_fleet',
      name: 'FleetMode Pro Fleet',
      shortName: 'Pro Fleet',
      description: 'For higher-volume operations needing audit depth and advanced evidence tools.',
      monthlyFee: 299,
      yearlyFee: betaAnnualFee(299),
      seats: '10 team seats',
      recommendedFor: 'Multi-admin fleet operations',
      monthlySoftLimit: 800,
      dailySoftLimit: 150,
      features: {
        aiExtraction: true,
        bulkUpload: true,
        riskScoring: true,
        dvlaLookup: true,
        manualLiabilityTransfer: true,
        basicDashboard: true,
        advancedReports: true,
        aiAppeals: true,
        oneClickTransfer: true,
        prioritySupport: true,
        concierge: false,
        multipleTeamMembers: true,
        agreementAudit: true,
      },
    },
    CONCIERGE: {
      id: 'concierge',
      name: 'FleetMode Concierge',
      shortName: 'Concierge',
      description: 'For operators that want PCN workflow support and agreed managed-service terms.',
      monthlyFee: null,
      yearlyFee: null,
      seats: 'Unlimited team seats',
      recommendedFor: 'High-volume or managed support needs',
      monthlySoftLimit: null,
      dailySoftLimit: null,
      features: {
        aiExtraction: true,
        bulkUpload: true,
        riskScoring: true,
        dvlaLookup: true,
        manualLiabilityTransfer: true,
        basicDashboard: true,
        advancedReports: true,
        aiAppeals: true,
        oneClickTransfer: true,
        prioritySupport: true,
        concierge: true,
        multipleTeamMembers: true,
        agreementAudit: true,
      },
    },
  } satisfies Record<string, PlanConfig>,

  STRIPE_PRODUCTS: {
    STARTER_MONTHLY: import.meta.env.VITE_STRIPE_PRICE_STARTER_MONTHLY || '',
    STARTER_YEARLY: import.meta.env.VITE_STRIPE_PRICE_STARTER_YEARLY || '',
    GROWTH_MONTHLY: import.meta.env.VITE_STRIPE_PRICE_GROWTH_MONTHLY || '',
    GROWTH_YEARLY: import.meta.env.VITE_STRIPE_PRICE_GROWTH_YEARLY || '',
    PRO_FLEET_MONTHLY: import.meta.env.VITE_STRIPE_PRICE_PRO_FLEET_MONTHLY || import.meta.env.VITE_STRIPE_PRICE_PRO_MONTHLY || '',
    PRO_FLEET_YEARLY: import.meta.env.VITE_STRIPE_PRICE_PRO_FLEET_YEARLY || import.meta.env.VITE_STRIPE_PRICE_PRO_YEARLY || '',
  },

  BILLING: {
    TRIAL_DAYS: 7,
    TRIAL_PCN_LIMIT: 3,
    BETA_ANNUAL_DISCOUNT_PERCENT,
    GRACE_PERIOD_DAYS: 3,
    INVOICE_DUE_DAYS: 7,
  },
} as const;

export const normalizePlanId = (planId: AnyPlanId | null | undefined): PlanId => {
  if (planId === 'growth') return 'growth';
  if (planId === 'pro_fleet' || planId === 'pro') return 'pro_fleet';
  if (planId === 'concierge' || planId === 'managed') return 'concierge';
  return 'starter';
};

export const getPlanConfig = (planId: AnyPlanId | null | undefined): PlanConfig => {
  switch (normalizePlanId(planId)) {
    case 'growth':
      return PRICING.PLANS.GROWTH;
    case 'pro_fleet':
      return PRICING.PLANS.PRO_FLEET;
    case 'concierge':
      return PRICING.PLANS.CONCIERGE;
    case 'starter':
    default:
      return PRICING.PLANS.STARTER;
  }
};

export const getVisiblePlans = (): PlanConfig[] => [
  PRICING.PLANS.STARTER,
  PRICING.PLANS.GROWTH,
  PRICING.PLANS.PRO_FLEET,
  PRICING.PLANS.CONCIERGE,
];

export const calculateEstimatedCost = (
  planId: AnyPlanId,
  billingCycle: BillingCycle
): number => {
  const plan = getPlanConfig(planId);
  if (plan.monthlyFee === null || plan.yearlyFee === null) return 0;
  return billingCycle === 'monthly' ? plan.monthlyFee : plan.yearlyFee / 12;
};

export const getStandardAnnualFee = (planId: AnyPlanId): number | null => {
  const plan = getPlanConfig(planId);
  if (plan.monthlyFee === null) return null;
  return plan.monthlyFee * 12;
};

export const getBetaAnnualSavings = (planId: AnyPlanId): number | null => {
  const plan = getPlanConfig(planId);
  const standardAnnualFee = getStandardAnnualFee(planId);
  if (standardAnnualFee === null || plan.yearlyFee === null) return null;
  return standardAnnualFee - plan.yearlyFee;
};

export const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
    maximumFractionDigits: Number.isInteger(amount) ? 0 : 2,
  }).format(amount);
};

export const getStripePriceId = (
  planId: AnyPlanId,
  billingCycle: BillingCycle
): string => {
  const plan = normalizePlanId(planId);

  if (plan === 'starter') {
    return billingCycle === 'monthly'
      ? PRICING.STRIPE_PRODUCTS.STARTER_MONTHLY
      : PRICING.STRIPE_PRODUCTS.STARTER_YEARLY;
  }

  if (plan === 'growth') {
    return billingCycle === 'monthly'
      ? PRICING.STRIPE_PRODUCTS.GROWTH_MONTHLY
      : PRICING.STRIPE_PRODUCTS.GROWTH_YEARLY;
  }

  if (plan === 'pro_fleet') {
    return billingCycle === 'monthly'
      ? PRICING.STRIPE_PRODUCTS.PRO_FLEET_MONTHLY
      : PRICING.STRIPE_PRODUCTS.PRO_FLEET_YEARLY;
  }

  return '';
};
